
import React, { useImperativeHandle, forwardRef, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import Dice from "./Dice";
import { AnimatePresence, motion } from "framer-motion";
import { useRollingState } from "@/hooks/useRollingState";
import { PromptStage } from "@/hooks/dice/useRollerState";
import Welcome from "./dice/Welcome";
import ResultSection from "./dice/ResultSection";

export interface DiceRollerRef {
  triggerRoll: () => void;
  advanceToStage: (stage: Exclude<PromptStage, "none">) => void;
  getCurrentStage: () => PromptStage;
}

interface DiceRollerProps {
  className?: string;
  onDiceRoll?: (value: number) => void;
  onPromptStageChange?: (stage: PromptStage) => void;
  isJournalMode?: boolean;
  hideWelcomeText?: boolean;
  hasDiceRolled?: boolean;
}

const DiceRoller = forwardRef<DiceRollerRef, DiceRollerProps>(
  ({ className, onDiceRoll, onPromptStageChange, isJournalMode = false, hideWelcomeText = false, hasDiceRolled: externalHasRolled = false }, ref) => {
    const {
      diceValue,
      rollerState,
      promptStage,
      startRolling,
      rollAgain,
      advancePrompt,
      reRollPrompt,
      activateTwist,
      isRolling,
      showPrompts,
      twistActive,
      isSpinning,
      rerollCount,
      selectLensFocus,
      handleFeedback,
      lensType,
      shouldShowLensFocus,
      setPromptStage,
      setRollerState
    } = useRollingState(onDiceRoll);

    const [hasRolled, setHasRolled] = useState(externalHasRolled);

    // Simple sync with external hasRolled prop
    useEffect(() => {
      setHasRolled(externalHasRolled);
    }, [externalHasRolled]);

    // SIMPLIFIED: Only notify parent of stage changes, don't sync back
    useEffect(() => {
      if (onPromptStageChange && promptStage !== "none") {
        console.log("DiceRoller: Notifying parent of prompt stage change:", promptStage);
        onPromptStageChange(promptStage);
      }
    }, [promptStage, onPromptStageChange]);

    // FIX: For journal mode, DON'T automatically set to result state - let parent control
    useEffect(() => {
      if (isJournalMode && diceValue > 0 && !hasRolled) {
        console.log("DiceRoller: Journal mode - dice value exists but hasRolled is false, setting hasRolled to true");
        setHasRolled(true);
      }
    }, [isJournalMode, diceValue, hasRolled]);

    // Handle successful dice roll
    useEffect(() => {
      if (diceValue > 0 && rollerState === "result") {
        console.log("DiceRoller: Dice rolled successfully, value:", diceValue);
        setHasRolled(true);
      }
    }, [diceValue, rollerState]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      triggerRoll: () => {
        console.log("DiceRoller: External trigger for dice roll");
        startRolling();
      },
      advanceToStage: (stage: Exclude<PromptStage, "none">) => {
        console.log("DiceRoller: External request to advance to stage:", stage);
        if (rollerState !== "result") {
          setRollerState("result");
        }
        setPromptStage(stage);
        setHasRolled(true);
      },
      getCurrentStage: () => promptStage
    }));

    console.log("DiceRoller render:", { rollerState, promptStage, diceValue, hasRolled, isJournalMode });

    const shouldShowPromptSection = (showPrompts || promptStage !== "none") && hasRolled && diceValue > 0;

    return (
      <div className={cn(
        "flex flex-col items-center gap-6 p-6 max-w-md mx-auto relative",
        className
      )}>
        {!hideWelcomeText && (
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-2">Unstuck</h2>
            <p className="text-sm italic mb-1">A magic tool to break down walls and build new journeys.</p>
            <p className="text-base">Roll the dice for journaling prompts to break through stuck points</p>
          </div>
        )}
        
        <AnimatePresence mode="wait">
          {rollerState === "welcome" ? (
            <Welcome startRolling={startRolling} />
          ) : (
            <motion.div 
              key="dice"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative"
            >
              {hasRolled && diceValue > 0 ? (
                <Dice 
                  value={diceValue} 
                  rolling={isRolling || isSpinning} 
                  size={120}
                  className="mb-6"
                  onClick={rollerState === "rolling" ? undefined : startRolling}
                />
              ) : (
                <div 
                  className="flex items-center justify-center w-[140px] h-[140px] mb-6 cursor-pointer"
                  onClick={startRolling}
                >
                  <div className="bg-[#FBF3F1] rounded-xl p-5 shadow-lg transition-opacity duration-200 hover:opacity-90 text-center">
                    <span className="text-[#003F6B] font-semibold">Ready to roll</span>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {shouldShowPromptSection && (
            <ResultSection 
              diceValue={diceValue}
              promptStage={promptStage}
              rollAgain={rollAgain}
              advancePrompt={isJournalMode ? undefined : advancePrompt}
              reRollPrompt={isJournalMode ? undefined : reRollPrompt}
              twistActive={twistActive}
              activateTwist={isJournalMode ? undefined : activateTwist}
              isSpinning={isSpinning}
              rerollCount={rerollCount}
              onFeedback={handleFeedback}
              onSelectLens={selectLensFocus}
              lensType={lensType}
              shouldShowLensFocus={shouldShowLensFocus}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }
);

DiceRoller.displayName = "DiceRoller";

export default DiceRoller;
